<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import CampaignsAPI from 'dashboard/api/campaigns';
import {
  emptyDynamicFilter,
  dynamicFilterQuery,
  dynamicFilterEntry,
  dynamicFilterSummary,
  dynamicFilterFromAudience,
} from 'dashboard/helper/campaignDynamicFilter';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';

// Owner order 2026-09-14 (campaign audit, option 5): a scheduled WhatsApp
// campaign can be renamed, re-targeted and rescheduled until it starts
// sending. The template itself is not editable here: changing the message
// means a new campaign, which is cheap before anything was sent.
const props = defineProps({
  selectedCampaign: {
    type: Object,
    default: null,
  },
});

const { t } = useI18n();
const store = useStore();
const dialogRef = ref(null);
const labels = useMapGetter('labels/getLabels');
const uiFlags = useMapGetter('campaigns/getUIFlags');
const isUpdating = computed(() => Boolean(uiFlags.value?.isUpdating));

const state = reactive({
  title: '',
  scheduledAt: '',
  selectedAudience: [],
  selectedStages: [],
  dynamic: emptyDynamicFilter(),
  assistantNotes: '',
});
// Once a campaign has started sending, only the assistant's notes may still
// change (replies arrive long after sending); the server enforces the same.
const locked = computed(
  () =>
    props.selectedCampaign &&
    props.selectedCampaign.campaign_status !== 'active'
);
const audience = ref(null);
// The dynamic filter is kept as saved (2026-09-29); it is edited by making a
// new campaign, like the template.
const dynamicQuery = computed(() => dynamicFilterQuery(state.dynamic));
const dynamicSummary = computed(() =>
  dynamicFilterSummary(state.dynamic, (key, params) =>
    t(`CAMPAIGN.WHATSAPP.WIZARD.AUDIENCE.DYNAMIC.${key}`, params)
  )
);
const submitError = ref('');

const toLocalInput = unix => {
  if (!unix) return '';
  const d = new Date(unix * 1000);
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const fillFromCampaign = campaign => {
  state.title = campaign?.title || '';
  state.scheduledAt = toLocalInput(campaign?.scheduled_at);
  state.selectedAudience = (campaign?.audience || [])
    .filter(item => item.type === 'Label')
    .map(item => item.id);
  state.selectedStages = (campaign?.audience || [])
    .filter(item => item.type === 'LifecycleStage')
    .map(item => item.id);
  state.dynamic = dynamicFilterFromAudience(campaign?.audience);
  state.assistantNotes = campaign?.assistant_notes || '';
  audience.value = null;
  submitError.value = '';
};

watch(() => props.selectedCampaign, fillFromCampaign, { immediate: true });

const audienceList = computed(() =>
  (labels.value || []).map(label => ({ value: label.id, label: label.title }))
);
const lifecycleReady = useMapGetter('customerLifecycle/isReady');
const lifecycleStages = useMapGetter('customerLifecycle/getActiveStages');
const stageList = computed(() =>
  (lifecycleStages.value || []).map(stage => ({
    value: stage.id,
    label: `${stage.emoji || ''} ${stage.name}`.trim(),
  }))
);

let audienceRequest = 0;
watch(
  () => [
    [...state.selectedAudience],
    [...(state.selectedStages || [])],
    dynamicQuery.value,
  ],
  async ([ids, stageIds, dynamic]) => {
    if (!ids.length && !stageIds.length && !dynamic) {
      audience.value = null;
      return;
    }
    audienceRequest += 1;
    const seq = audienceRequest;
    try {
      const { data } = await CampaignsAPI.audiencePreview(
        ids,
        stageIds,
        dynamic
      );
      if (seq === audienceRequest) audience.value = data.payload;
    } catch {
      if (seq === audienceRequest) audience.value = null;
    }
  },
  { deep: true }
);

const audienceMessage = computed(() => {
  if (!audience.value) return t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.HINT');
  if (audience.value.total === 0)
    return t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.EMPTY');
  return t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.COUNT', {
    total: audience.value.total,
    reachable: audience.value.reachable,
    withoutPhone: audience.value.without_phone,
    blocked: audience.value.blocked,
    optedOut: audience.value.opted_out,
  });
});

const isInvalid = computed(() =>
  locked.value
    ? false
    : !state.title.trim() ||
      !state.scheduledAt ||
      (!state.selectedAudience.length &&
        !(state.selectedStages || []).length &&
        !dynamicQuery.value) ||
      (audience.value && audience.value.reachable === 0)
);

const handleConfirm = async () => {
  if (isInvalid.value || isUpdating.value || !props.selectedCampaign?.id)
    return;
  submitError.value = '';
  try {
    if (locked.value) {
      await store.dispatch('campaigns/update', {
        id: props.selectedCampaign.id,
        assistant_notes: (state.assistantNotes || '').trim(),
      });
      useAlert(t('CAMPAIGN.WHATSAPP.EDIT.API.SUCCESS_MESSAGE'));
      dialogRef.value?.close();
      return;
    }
    await store.dispatch('campaigns/update', {
      id: props.selectedCampaign.id,
      title: state.title.trim(),
      assistant_notes: (state.assistantNotes || '').trim(),
      scheduled_at: new Date(state.scheduledAt).toISOString(),
      audience: [
        ...state.selectedAudience.map(id => ({ id, type: 'Label' })),
        ...(state.selectedStages || []).map(id => ({
          id,
          type: 'LifecycleStage',
        })),
        ...[dynamicFilterEntry(state.dynamic)].filter(Boolean),
      ],
    });
    useAlert(t('CAMPAIGN.WHATSAPP.EDIT.API.SUCCESS_MESSAGE'));
    dialogRef.value?.close();
  } catch (error) {
    submitError.value =
      error?.message || t('CAMPAIGN.WHATSAPP.EDIT.API.ERROR_MESSAGE');
  }
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('CAMPAIGN.WHATSAPP.EDIT.TITLE')"
    :description="t('CAMPAIGN.WHATSAPP.EDIT.DESCRIPTION')"
    :confirm-button-label="t('CAMPAIGN.WHATSAPP.EDIT.CONFIRM')"
    :is-loading="isUpdating"
    :disable-confirm-button="isUpdating || isInvalid"
    @confirm="handleConfirm"
  >
    <div class="flex flex-col gap-4">
      <p v-if="locked" class="mb-0 text-xs text-n-amber-11">
        {{ t('CAMPAIGN.WHATSAPP.EDIT.LOCKED_NOTES_ONLY') }}
      </p>
      <Input
        v-model="state.title"
        :disabled="locked"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.LABEL')"
      />
      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.ASSISTANT_NOTES.LABEL') }}
        <textarea
          v-model="state.assistantNotes"
          rows="3"
          maxlength="2000"
          :placeholder="
            t('CAMPAIGN.WHATSAPP.CREATE.FORM.ASSISTANT_NOTES.PLACEHOLDER')
          "
          class="mb-0 rounded-lg border border-n-weak bg-n-alpha-black2 px-2 py-1 text-sm"
        />
        <span class="text-xs text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.ASSISTANT_NOTES.HINT') }}
        </span>
      </label>
      <div v-if="!locked" class="flex flex-col gap-1">
        <label class="mb-0.5 text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL') }}
        </label>
        <TagMultiSelectComboBox
          v-model="state.selectedAudience"
          :options="audienceList"
          :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL')"
          :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.PLACEHOLDER')"
          :message="audienceMessage"
          :message-type="
            audience && audience.reachable === 0 ? 'error' : 'info'
          "
          class="[&>div>button]:bg-n-alpha-black2"
        />
        <TagMultiSelectComboBox
          v-if="lifecycleReady"
          v-model="state.selectedStages"
          :options="stageList"
          :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.STAGES.LABEL')"
          :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.STAGES.PLACEHOLDER')"
          :message="t('CAMPAIGN.WHATSAPP.CREATE.FORM.STAGES.HINT')"
          message-type="info"
        />
        <p v-if="dynamicSummary" class="mb-0 text-xs text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.AUDIENCE.TABS.DYNAMIC') }}:
          {{ dynamicSummary }}
        </p>
      </div>
      <Input
        v-if="!locked"
        v-model="state.scheduledAt"
        type="datetime-local"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.LABEL')"
      />
      <p v-if="submitError" class="mb-0 text-sm text-n-ruby-11">
        {{ submitError }}
      </p>
    </div>
  </Dialog>
</template>
